import React, {
  memo,
  useState,
  createContext,
  useContext,
  useEffect,
} from 'react';
import {connect} from 'react-redux';
import Header from './Header';
import {ProfiledRiskPaymentModal} from '../routes/ProfiledRisk/ProfiledRiskPaymentModal';
import GettingStartedWidget from 'routes/ProfiledRisk/GetStartedWidget';

export const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardLayout');
  }
  return context;
};

const MemoizedHeader = memo(Header);

const STORAGE_KEY_CLOSED = 'profiled_risk_widget_closed';

function DashboardLayout({
  children,
  action,
  headerText = '',
  topPadding = 'pt-8 sm:pt-0',
  leftMargin = 'ml-4 sm:ml-8 lg:ml-8',
  xlLeftMargin = 'xl:ml-8',
  xlRightPadding = 'xl:pr-8',
  bg = 'bg-white',
  fullScreen,
  breadCrumbs,
  overFlow = 'overflow-x-hidden',
  className,
  auth: {userDetails},
}) {
  const isProfiledRisk = localStorage.subdomain?.includes('profiledrisk');

  const [showSetupWidget, setShowSetupWidget] = useState(false);

  useEffect(() => {
    if (!isProfiledRisk) return;

    const setupGuide = userDetails?.company?.setup_guide;
    const isComplete =
      (setupGuide?.step1 ?? false) &&
      (setupGuide?.step2 ?? false) &&
      (setupGuide?.step3 ?? false);

    const hasClosedManually =
      localStorage.getItem(STORAGE_KEY_CLOSED) === 'true';

    if (!isComplete && !hasClosedManually) {
      setShowSetupWidget(true);
    } else if (isComplete) {
      setShowSetupWidget(false);
    }
  }, [userDetails, isProfiledRisk]);

  const closeWidget = () => {
    setShowSetupWidget(false);
    localStorage.setItem(STORAGE_KEY_CLOSED, 'true');
  };

  const openWidget = () => {
    setShowSetupWidget(true);
    localStorage.removeItem(STORAGE_KEY_CLOSED);
  };

  return (
    <DashboardContext.Provider
      value={{showSetupWidget, openWidget, closeWidget}}
    >
      <div
        className={`flex flex-col min-h-screen ${
          isProfiledRisk ? 'font-sans' : ''
        }`}
      >
        <main
          className={`lg:ml-[16.4%] ${bg} min-h-screen pr-4 ${overFlow} ${xlRightPadding}`}
        >
          <div className="relative z-20">
            <MemoizedHeader headerText={headerText} breadCrumbs={breadCrumbs} />
          </div>

          <section
            className={`${topPadding} mt-6 sm:mt-[51px] ${leftMargin} ${xlLeftMargin} ${
              fullScreen ? 'mb-10 md:mb-0' : 'mb-16'
            } relative z-10 ${className}`}
          >
            {action ? (
              <div className="flex items-center justify-between gap-6 mt-6 mb-6 md:gap-0 sm:mt-0">
                {action}
              </div>
            ) : null}
            {children}
          </section>

          {isProfiledRisk && (
            <>
              <ProfiledRiskPaymentModal />
              <GettingStartedWidget
                isOpen={showSetupWidget}
                onClose={closeWidget}
              />
            </>
          )}
        </main>
      </div>
    </DashboardContext.Provider>
  );
}

export default connect(state => state)(memo(DashboardLayout));
